import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { Routes, Route, Navigate, NavLink, Link, useLocation } from 'react-router-dom';
import Home from './screens/home/home';
import CV from './screens/cv/cv';
import AppsPage from './screens/apps/AppsPage';
import BirdifyPage from './screens/apps/BirdifyPage';
import BirdifyPrivacyPage from './screens/apps/BirdifyPrivacyPage';
import NumbCrunchPage from './screens/apps/NumbCrunchPage';
import NumbCrunchPrivacyPage from './screens/apps/NumbCrunchPrivacyPage';
import QrCodePage from './screens/apps/QrCodePage';
import QrCodeLicensesPage from './screens/apps/QrCodeLicensesPage';
import NotFound from './screens/not-found/NotFound';
import { canonicalAppsPath } from './screens/apps/routes';
import { setCurrentPage, trackClick, trackEvent } from './firebase/analytics';
import AnalyticsControls from './analytics/AnalyticsControls';
import WebsitePrivacy from './screens/privacy/privacy';
import ThemeToggle from './design-system/ThemeToggle';
import { blogEnvironment } from './firebase/firebase-config';

const BlogPage = lazy(() => import('./screens/blog/BlogPage'));
const BlogPostPage = lazy(() => import('./screens/blog/BlogPostPage'));
const OwnerLogin = lazy(() => import('./screens/admin/OwnerLogin'));
const BlogAdmin = lazy(() => import('./screens/admin/BlogAdmin'));
const BlogEditor = lazy(() => import('./screens/admin/BlogEditor'));

class RouteErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <main id="main" className="kds-stack"><h1 className="kds-headline-lg">This page could not load.</h1><p>Check your connection and reload to try again.</p><div><button className="kds-btn kds-btn--tonal" onClick={() => window.location.reload()}>Reload page</button></div></main>;
    return this.props.children;
  }
}

function App() {

  const location = useLocation();
  const legacyAdmin = /^\/admin\/blog(?:\/|$)/.test(location.pathname) ? location.pathname.replace(/^\/admin\/blog/, '/blog/admin') : null;
  const redirectTo = legacyAdmin || canonicalAppsPath(location.pathname);

  const { pathname, hash } = location;
  const previousPathname = useRef(null);

  useLayoutEffect(() => {
    if (redirectTo || previousPathname.current === pathname) return;
    previousPathname.current = pathname;

    // Keep fragment links native; reset new pages before paint, without the
    // document's smooth-scroll animation or resets on query/state changes.
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, redirectTo]);

  useLayoutEffect(() => {
    // Suspend private collection before passive effects; log a public page view when the path changes. Hash/query/history-state changes on
    // the same page (e.g. policy section anchors) do not count, and aliases that
    // are about to be redirected are skipped so the canonical path is counted once.
    if (redirectTo) return;
    setCurrentPage(pathname);
  }, [pathname, redirectTo]);

  useEffect(() => {
    if (redirectTo) return;
    let recorded = false;
    const onScroll = () => {
      const height = document.documentElement.scrollHeight;
      if (!recorded && height > window.innerHeight && (window.scrollY + window.innerHeight) / height >= 0.9) {
        recorded = trackEvent('scroll', { percent_scrolled: 90 });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname, redirectTo]);

  if (redirectTo) {
    return <Navigate to={{ pathname: redirectTo, search: location.search, hash: location.hash }} replace />;
  }

  const isHome = pathname === '/';
  const AppBar = isHome ? 'div' : 'header';

  return (
    <div className="kds-app">
      {/* Signature tonal wash; purely decorative and never focusable. */}
      <div className="kds-backdrop" aria-hidden="true" />

      <a className="kds-skip-link" href="#main">Skip to content</a>

      <AppBar className={`kds-appbar${isHome ? ' kds-appbar--home' : ''}`}>
        {!isHome && <nav className="kds-site-nav" aria-label="Main navigation">
          <NavLink className="kds-site-brand" to="/" end>Keval<span aria-hidden="true">.</span></NavLink>
          <div className="kds-site-links">
            <NavLink to="/cv">CV</NavLink>
            <NavLink to="/apps">Portfolio</NavLink>
            <NavLink to="/blog">Blog</NavLink>
          </div>
        </nav>}
        <ThemeToggle onClick={trackClick('shell', 'theme_toggle')} />
      </AppBar>

      {blogEnvironment.target === 'preview' && <aside className="kds-callout kds-body-sm" aria-label="Preview notice">Read-only preview · Blog contains sample articles. Sign-in, publishing and uploads are disabled. <Link to="/blog" className="kds-link">View sample posts</Link></aside>}
      <AnalyticsControls>
        {/* Keyed on the path so each route replays the entrance once. */}
        <div className={`kds-page${/^\/(admin|blog\/)/.test(pathname) || pathname.endsWith('/privacy') ? '' : ' kds-route-enter'}`} key={pathname}>
          <RouteErrorBoundary><Suspense fallback={<main id="main"><h1 className="kds-headline-lg">Loading page…</h1><p role="status">Please wait.</p></main>}>

          {/* Define Routes */}
          <Routes>

            {/* Home Route */}
            <Route path="/" element={<Home />} />

            {/* CV Route */}
            <Route path="/cv" element={<CV />} />
            <Route path="/privacy" element={<WebsitePrivacy />} />

            {/* Portfolio section: existing canonical URLs remain stable. */}
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/apps/birdify" element={<BirdifyPage />} />
            <Route path="/apps/birdify/privacy" element={<BirdifyPrivacyPage />} />
            <Route path="/apps/numbcrunch" element={<NumbCrunchPage />} />
            <Route path="/apps/numbcrunch/privacy" element={<NumbCrunchPrivacyPage />} />
            <Route path="/apps/qr-code" element={<QrCodePage />} />
            <Route path="/apps/qr-code/licenses" element={<QrCodeLicensesPage />} />
            <Route path="/blog" caseSensitive element={<BlogPage />} />
            <Route path="/blog/:slug" caseSensitive element={<BlogPostPage />} />
            <Route path="/blog/admin" caseSensitive element={<OwnerLogin />} />
            <Route path="/blog/admin/posts" caseSensitive element={<BlogAdmin />} />
            <Route path="/blog/admin/new" caseSensitive element={<BlogEditor />} />
            <Route path="/blog/admin/:slug/edit" caseSensitive element={<BlogEditor />} />
            <Route path="*" element={<NotFound />} />

          </Routes>
          </Suspense></RouteErrorBoundary>
        </div>
      </AnalyticsControls>
    </div>
  );
}

export default App;
